import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { MarketCredit } from '@sorare/core/src/atoms/icons/MarketCredit';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { CornerBadge } from '@sorare/core/src/components/CornerBadge';

import { useGetAuctionDetails } from 'hooks/offers/useGetAuctionDetails';
import { useIsPrimaryBuyLive } from 'hooks/primaryBuy/useIsPrimaryBuyLive';

import { MarketCreditCornerBadgeWithDiscount } from './MarketCreditCornerBadgeWithDiscount';
import type { MarketCreditCornerBadge_anyCard } from './__generated__/index.graphql';

const Label = styled(LabelS)`
  white-space: nowrap;
`;
type Props = {
  canUseConversionCredit: boolean;
  anyCard: MarketCreditCornerBadge_anyCard;
};

export const MarketCreditCornerBadge = ({
  anyCard,
  canUseConversionCredit,
}: Props) => {
  const auction = useGetAuctionDetails(anyCard.latestEnglishAuction);
  const primaryBuy = useIsPrimaryBuyLive(anyCard);

  if (primaryBuy && canUseConversionCredit)
    return (
      <MarketCreditCornerBadgeWithDiscount
        price={primaryBuy.price}
        sport={anyCard.sport}
      />
    );
  if (auction && canUseConversionCredit)
    return (
      <CornerBadge color="var(--c-score-high)">
        <MarketCredit />
        <Label bold color="var(--c-black)">
          <FormattedMessage
            id="PlayerDrawerToken.marketCredit.cornerBadge"
            defaultMessage="Credit eligible"
          />
        </Label>
      </CornerBadge>
    );
  return null;
};

MarketCreditCornerBadge.fragments = {
  anyCard: gql`
    fragment MarketCreditCornerBadge_anyCard on AnyCardInterface {
      slug
      sport
      latestEnglishAuction {
        id
        ...useGetAuctionDetails_auction
      }
      ...useIsPrimaryBuyLive_anyCard
    }
    ${useGetAuctionDetails.fragments.auction}
    ${useIsPrimaryBuyLive.fragments.anyCard}
  ` as TypedDocumentNode<MarketCreditCornerBadge_anyCard>,
};
